import { z } from 'zod';
import { ActiveTx } from './active-tx';
import { ProfileState } from './profile';

const nonneg = z.number().nonnegative();
const count = z.number().int().nonnegative();

/** 이벤트루프 지연 분위수(ms). perf_hooks.monitorEventLoopDelay */
export const LoopStats = z.object({
  p50: nonneg,
  p99: nonneg,
  max: nonneg,
});
export type LoopStats = z.infer<typeof LoopStats>;

/**
 * 힙·RSS(bytes). process.memoryUsage.
 * `limitBytes`는 V8 힙 상한(`v8.getHeapStatistics().heap_size_limit`, 22-C회차). 사용률의 분모는
 * 이것이다. `totalBytes`(확보한 힙)를 분모로 쓰면 비율이 늘 80~97%로 나와 헛경보가 났다.
 * 옛 에이전트는 보내지 않으므로 선택이고, 없으면 `totalBytes` 기준으로 되돌아간다(`@apm/core` `heapUsedRatio`).
 */
export const HeapStats = z.object({
  usedBytes: count,
  totalBytes: count,
  rssBytes: count,
  limitBytes: count.optional(),
});
export type HeapStats = z.infer<typeof HeapStats>;

/** 직전 1초 동안의 GC 합계. perf_hooks PerformanceObserver('gc') */
export const GcStats = z.object({
  count,
  totalMs: nonneg,
  maxMs: nonneg,
});
export type GcStats = z.infer<typeof GcStats>;

/** pg 커넥션 풀 게이지. 풀이 없으면 생략 */
export const PoolStats = z.object({
  total: count,
  idle: count,
  waiting: count,
});
export type PoolStats = z.infer<typeof PoolStats>;

/**
 * JVM 런타임 게이지 (29회차). **보내는 쪽은 아직 없다** (2026-09-09 결정 D-3).
 *
 * 계약과 화면 자리만 연다. 표준 OTel Java agent 는 트레이스만 보내고, 이 값들을 채우려면
 * MBean(`ThreadMXBean`·`ClassLoadingMXBean`·`GarbageCollectorMXBean`)을 1초마다 읽어
 * `POST /rt/snapshot` 을 치는 작은 Java 송신기가 있어야 한다. 그것을 만들지는 회차 밖이다.
 *
 * `heap`·`gc`·`pool` 은 JVM 에도 그대로 대응이 있어(`MemoryMXBean`·`GarbageCollectorMXBean`·
 * `HikariPoolMXBean`) 새 칸을 만들지 않았다. 여기 있는 것은 **JVM 에만 있는 것**뿐이다.
 */
export const JvmStats = z.object({
  threads: z.object({ live: count, daemon: count, peak: count }),
  /** 지금 로드된 클래스 수. 계속 늘면 클래스 누수다 */
  classesLoaded: count,
  /** GC 풀별 누계. JVM 은 GC 가 여럿이라 하나로 합치면 어느 쪽이 도는지 모른다 */
  gcPools: z.array(z.object({ name: z.string().min(1), count, totalMs: nonneg })).optional(),
});
export type JvmStats = z.infer<typeof JvmStats>;

/**
 * .NET Framework 런타임 게이지 (36회차).
 *
 * **아직 이 칸을 보내는 쪽은 없다.** 자리만 연 것이고, 채우는 것은 `C:\Src\apm-dotnet` 의 IIS
 * 송신기가 할 후속 작업이다. 그 저장소 `main`(c854dfa)의 송신기는 공통 값
 * (`heap`·`gc`·`rate`·`activeTx`·`processStartedAt`·`agentVersion`)까지만 조립한다.
 * 배포 순서는 **계약 개방 → 관제 갱신 → 송신기 판올림**이고, 관제만 올리면 카드는 그대로 비어 있다.
 *
 * `jvm` 과 같은 규칙이다 - **그 런타임에만 있는 것**만 여기 둔다. `heap`·`gc` 는 .NET 에도
 * 대응이 있어(`Process.WorkingSet64`·`GC.CollectionCount`) 새 칸을 만들지 않았다.
 *
 * `pool` 은 지금까지처럼 **DB 커넥션 풀**이다. 화면이 그 자리에 "DB 커넥션 풀"이라고 글자로
 * 적으므로 .NET 스레드풀을 거기 실으면 화면이 거짓말을 한다. 그래서 스레드풀은 여기 따로 둔다.
 */
export const DotnetStats = z.object({
  /**
   * CLR 스레드풀. `ThreadPool.GetAvailableThreads`/`GetMaxThreads` 두 줄이다.
   * 가용이 0에 붙으면 IIS 앱이 느려지는 흔한 까닭이다
   */
  threadPool: z.object({
    worker: z.object({ available: count, max: count }),
    io: z.object({ available: count, max: count }),
  }),
  /** 이 프로세스의 CPU 사용률(%). `Process.TotalProcessorTime` 차분이라 코어 수로 나눈 값이다 */
  cpuPercent: nonneg.optional(),
  /**
   * `gc.totalMs`·`maxMs` 가 **근사**라는 표시.
   *
   * .NET Framework 4.8 의 공개 API 로는 GC 정지 시간을 못 잰다(`GetGCMemoryInfo` 는 .NET Core 3
   * 이상이다). 송신기는 성능 카운터 `% Time in GC` 에 창 길이를 곱한 값을 싣고, 한 번의 최대
   * 정지는 나눠 잴 수 없어 합계와 같은 값을 싣는다. 그 사실을 화면이 글자로 적게 하는 칸이다.
   * 진짜로 잰 런타임은 이 칸을 보내지 않는다
   */
  gcApproximate: z.boolean().optional(),
  /**
   * 세대별 수집 **누계** 횟수(`GC.CollectionCount(n)`). 직전 1초 값인 `gc.count` 와 다르다.
   * 2세대를 한 번 거두면 0·1세대 값도 함께 오르므로 **셋을 더하면 안 된다** - 0세대 값이 곧
   * 전체 수집 횟수이고, 2세대 값이 무거운 정리가 몇 번 돌았는지다.
   * 지금 화면은 이 값을 그리지 않는다(`jvm.gcPools` 와 같다)
   */
  gen: z.object({ '0': count, '1': count, '2': count }),
});
export type DotnetStats = z.infer<typeof DotnetStats>;

/** 직전 1초 동안 끝난 트랜잭션 수와 오류 수 */
export const RateStats = z.object({
  completed: count,
  errors: count,
});
export type RateStats = z.infer<typeof RateStats>;

/**
 * sse 타입은 연결이 수십 분 유지되므로 상위 N건 정렬에서 뺀다.
 * 건수와 최장 연결 시간만 보낸다.
 */
export const SseBucket = z.object({
  count,
  maxElapsedMs: nonneg,
});
export type SseBucket = z.infer<typeof SseBucket>;

/** `/rt/snapshot`은 인증이 없다. 받는 쪽 메모리를 지키는 상한 (57회차) */
export const SNAPSHOT_SERVICE_MAX = 4_096;
export const SNAPSHOT_INSTANCE_MAX = 256;
/** 버킷 하나의 건수 상한. 에이전트 `snapshot.topN` 기본 50보다 넉넉히 둔다 */
export const SNAPSHOT_BUCKET_MAX = 500;

const bucket = z.array(ActiveTx).max(SNAPSHOT_BUCKET_MAX);

/**
 * 에이전트가 1초마다 POST /rt/snapshot 으로 보내는 실시간 스냅샷.
 * 서버는 메모리 링버퍼에만 두고 저장하지 않는다.
 *
 * activeTx는 타입별 버킷이다. http·mcp·pdf·worker·slow는 경과시간 내림차순 상위 N건,
 * sse는 SseBucket으로 따로 보낸다.
 */
export const Snapshot = z.object({
  /** 스냅샷 시각. epoch ms */
  ts: z.number().int().nonnegative(),
  /**
   * 서비스 이름. 상한은 57회차에 뒀다. 서버는 1KB에서 잘라 받으므로(리뷰 R2) 그보다 넉넉히 두고,
   * 넘으면 거절한다
   */
  service: z.string().min(1).max(SNAPSHOT_SERVICE_MAX),
  /** 인스턴스 식별자. 호스트명 + pid 등. 넘으면 거절한다 (57회차) */
  instance: z.string().min(1).max(SNAPSHOT_INSTANCE_MAX),
  /** 목록 버킷은 각각 `SNAPSHOT_BUCKET_MAX`건까지다. 넘으면 거절한다 (57회차) */
  activeTx: z.object({
    http: bucket,
    mcp: bucket,
    pdf: bucket,
    worker: bucket,
    /**
     * 오래 걸리는 것이 정상인 요청 (28회차). **기본값이 있다** — 27회차까지의 에이전트는
     * 이 버킷을 안 보낸다. 없으면 빈 배열로 채워야 옛 에이전트의 스냅샷이 검증에 걸리지 않는다
     */
    slow: bucket.default([]),
    sse: SseBucket,
  }),
  loop: LoopStats,
  heap: HeapStats,
  gc: GcStats,
  pool: PoolStats.optional(),
  rate: RateStats,
  /**
   * CPU 프로파일러 상태 (8회차 결정 1). 프로파일러를 끈 에이전트는 보내지 않으므로 선택이다.
   * 화면은 값이 없으면 배지를 아예 그리지 않는다.
   */
  profile: ProfileState.optional(),
  /**
   * 대상 프로세스가 뜬 시각(epoch ms, 22-C회차 UI 피드백 9). 재시작·배포 시점이 이것이다.
   * 스냅샷마다 같은 값이 온다. 옛 에이전트는 보내지 않으므로 선택이다
   */
  processStartedAt: z.number().int().nonnegative().optional(),
  /** 붙어 있는 에이전트의 버전(`apps/agent/package.json`). 옛 에이전트는 보내지 않는다 */
  agentVersion: z.string().min(1).optional(),
  /** JVM 런타임 게이지 (29회차). 보내는 쪽이 아직 없어 지금은 언제나 비어 있다 */
  jvm: JvmStats.optional(),
  /**
   * .NET Framework 런타임 게이지 (36회차). **보내는 쪽이 아직 없어 지금은 언제나 비어 있다.**
   * 후속 apm-dotnet 판이 채운다
   */
  dotnet: DotnetStats.optional(),
});
export type Snapshot = z.infer<typeof Snapshot>;

/**
 * 서버가 **받는** 스냅샷 (29회차). 위와 `loop` 하나만 다르다 - 여기서는 선택이다.
 *
 * **JVM 에는 이벤트루프가 없다.** 이벤트루프 지연은 Node 런타임의 값이라 다른 런타임에서
 * 보내는 스냅샷에는 그 자리가 아예 없다. 0 을 채우면 "밀리지 않는다"는 거짓말이 되므로
 * 비워 둔다. 화면은 없는 값의 카드를 안 그리고, `loop_p99` 알림 규칙도 그 인스턴스를 건너뛴다.
 *
 * 위의 `Snapshot`(우리 Node 에이전트가 **만드는** 것)과 이것(저장·화면이 **받는** 것)을 가른
 * 이유는 `TX_TYPES`·`STORED_TX_TYPES` 와 같다. 에이전트는 지금까지처럼 `loop` 를 언제나
 * 보내므로 그쪽 타입을 넓힐 이유가 없고, 넓히면 에이전트 코드가 함께 바뀌어야 한다.
 * 에이전트를 손대는 회차가 오면 그때 하나로 합친다.
 */
export const ReceivedSnapshot = Snapshot.extend({ loop: LoopStats.optional() });
export type ReceivedSnapshot = z.infer<typeof ReceivedSnapshot>;
